import { Link } from "react-router-dom";
import { DONATE_URL, SUBSTACK_URL, MARK_URL, SOCIALS, STORE_URL } from "@/lib/api";
import { ArrowUpRight, Facebook, Linkedin, Youtube, Instagram } from "lucide-react";

const TikTok = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden {...props}>
    <path d="M16.5 3c.3 2.1 1.6 3.6 3.7 3.9v2.4c-1.3.1-2.5-.3-3.7-1v6.4c0 3.3-2.5 5.6-5.6 5.6-3 0-5.4-2.3-5.4-5.3 0-3.1 2.6-5.5 5.9-5.1v2.5c-.4-.1-.9-.2-1.3-.1-1.3.2-2.2 1.2-2.1 2.6.1 1.3 1.2 2.3 2.5 2.2 1.4-.1 2.3-1.1 2.3-2.6V3h3.2z" />
  </svg>
);

const iconFor = (name) => {
  if (name === "Facebook") return <Facebook size={18} />;
  if (name === "LinkedIn") return <Linkedin size={18} />;
  if (name === "YouTube") return <Youtube size={18} />;
  if (name === "Instagram") return <Instagram size={18} />;
  return <TikTok />;
};

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative bg-ink text-white grain overflow-hidden">
      <div className="relative z-10 max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 pt-20 pb-10">
        <div className="grid lg:grid-cols-12 gap-12 border-b border-white/15 pb-16">
          <div className="lg:col-span-6">
            <p className="label text-canary mb-6">Lift Every Voice</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Because the most impacted are often the most impactful.
            </h2>
            <div className="flex flex-wrap gap-4 mt-9">
              <a
                href={DONATE_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-donate-btn"
                className="inline-flex items-center gap-2 label bg-canary text-ink px-8 py-4 rounded-full hover:bg-white transition-colors duration-300"
              >
                Support our work <ArrowUpRight size={16} />
              </a>
              <a
                href={SUBSTACK_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-substack-btn"
                className="inline-flex items-center gap-2 label border border-white/40 px-8 py-4 rounded-full hover:bg-white hover:text-ink transition-colors duration-300"
              >
                Subscribe
              </a>
            </div>
          </div>

          <div className="lg:col-span-3 lg:col-start-8">
            <p className="label text-white/40 mb-5">Explore</p>
            <ul className="space-y-3 font-sans text-white/80">
              <li><Link to="/articles" className="hover:text-canary transition-colors">Articles</Link></li>
              <li><Link to="/photojournalism" className="hover:text-canary transition-colors">Photojournalism</Link></li>
              <li><Link to="/photo-of-the-month" data-testid="footer-potm-link" className="hover:text-canary transition-colors">Photo of the Month</Link></li>
              <li><Link to="/events" className="hover:text-canary transition-colors">Events</Link></li>
              <li><a href={STORE_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-shop-link" className="hover:text-canary transition-colors">Shop</a></li>
              <li><Link to="/partner" className="hover:text-canary transition-colors">Partner &amp; Sponsor</Link></li>
              <li><Link to="/sponsors" data-testid="footer-sponsors-link" className="hover:text-canary transition-colors">Our Sponsors</Link></li>
              <li><Link to="/about" className="hover:text-canary transition-colors">About &amp; Correspondents</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="label text-white/40 mb-5">Connect</p>
            <ul className="space-y-3 font-sans text-white/80">
              <li><Link to="/contact" data-testid="footer-contact-link" className="hover:text-canary transition-colors">Contact</Link></li>
              <li><a href={SUBSTACK_URL} target="_blank" rel="noopener noreferrer" className="hover:text-canary transition-colors">Substack</a></li>
              <li><a href="mailto:editor@thecanary.report" className="hover:text-canary transition-colors">Email</a></li>
              <li><Link to="/admin" data-testid="footer-admin-link" className="hover:text-canary transition-colors">Editor Login</Link></li>
            </ul>
          </div>
        </div>

        {/* Socials */}
        <div data-testid="footer-socials" className="border-b border-white/15 py-12 grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5">
            <p className="label text-canary mb-3">Let&rsquo;s be charm-ing together</p>
            <p className="font-display text-2xl sm:text-3xl leading-snug">
              Did you know a group of canaries is called a <span className="text-canary italic">charm?</span>
            </p>
            <p className="text-white/60 mt-3">Follow us and we&rsquo;ll follow you!</p>
          </div>
          <div className="lg:col-span-7 space-y-7">
            <div>
              <p className="label text-white/40 mb-3">The Canary Report</p>
              <div className="flex flex-wrap gap-3">
                {SOCIALS.brand.map((s) => (
                  <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer"
                    data-testid={`social-brand-${s.name.toLowerCase()}`} aria-label={`The Canary Report on ${s.name}`}
                    className="h-11 w-11 grid place-items-center rounded-full border border-white/25 hover:bg-canary hover:text-ink hover:border-canary transition-colors duration-300">
                    {iconFor(s.name)}
                  </a>
                ))}
              </div>
            </div>
            <div>
              <p className="label text-white/40 mb-3">Our founder &amp; editor-in-chief, Summer Nettles</p>
              <div className="flex flex-wrap gap-3">
                {SOCIALS.founder.map((s) => (
                  <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer"
                    data-testid={`social-founder-${s.name.toLowerCase()}`} aria-label={`Summer Nettles on ${s.name}`}
                    className="h-11 w-11 grid place-items-center rounded-full border border-white/25 hover:bg-canary hover:text-ink hover:border-canary transition-colors duration-300">
                    {iconFor(s.name)}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-8 text-white/50 text-sm">
          <div className="flex items-center gap-3">
            <img src={MARK_URL} alt="The Canary Report" className="h-12 w-12" />
            <p>© {new Date().getFullYear()} The Canary Report. Community-centered journalism.</p>
          </div>
          <p className="label text-white/40">Denver · Colorado</p>
        </div>
      </div>
    </footer>
  );
}
